import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, FileText, Download, Loader2, Search, X } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { fetchWithAuth } from '../../../services/api';
import { SupportManual } from '../../../services/supportManualService';

interface ManuaisPanelProps {
  onBack?: () => void;
  lightTheme?: boolean;
}

const ManuaisPanel: React.FC<ManuaisPanelProps> = ({ onBack, lightTheme }) => {
  const [manuals, setManuals] = useState<SupportManual[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<SupportManual | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchWithAuth('/api/v1/support-manuals')
      .then(async (res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        return (data.manuals ?? []) as SupportManual[];
      })
      .then(setManuals)
      .catch((err) => setError(`Não foi possível carregar os manuais. (${err.message})`))
      .finally(() => setLoading(false));
  }, []);

  const filteredManuals = manuals.filter(manual => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return manual.title.toLowerCase().includes(q) || manual.description?.toLowerCase().includes(q);
  });

  // PDF viewer overlay
  if (viewing) {
    return (
      <div className="flex flex-col h-full overflow-hidden bg-gray-950">
        {/* Viewer header */}
        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-800 flex-shrink-0 bg-gray-900">
          <button
            onClick={() => setViewing(null)}
            className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <FileText className="h-4 w-4 text-red-400 flex-shrink-0" />
          <p className="text-sm font-medium text-gray-100 truncate flex-1">{viewing.title}</p>
          <a
            href={viewing.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-white transition-colors flex-shrink-0"
          >
            <Download className="h-3.5 w-3.5" />
            Baixar
          </a>
          <button
            onClick={() => setViewing(null)}
            className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors text-gray-500 hover:text-white ml-1"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* PDF iframe */}
        <div className="flex-1 min-h-0">
          <iframe
            src={`${viewing.file_url}#toolbar=0&navpanes=0`}
            className="w-full h-full border-0"
            title={viewing.title}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col h-full overflow-hidden', lightTheme ? 'bg-white' : 'bg-gray-950')}>
      {/* Banner — always black, independent of theme */}
      <div className="relative flex-shrink-0 overflow-hidden bg-black">
        {/* Decorative glow accents */}
        <div className="pointer-events-none absolute -top-20 -right-16 h-56 w-56 rounded-full bg-blue-600/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-10 h-48 w-48 rounded-full bg-indigo-600/10 blur-3xl" />

        <div className="relative px-5 pt-4 pb-6">
          <div className="flex items-center justify-between">
            {onBack ? (
              <button
                onClick={onBack}
                className="p-1.5 -ml-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            ) : <span />}

            {/* Brand lockup — right side, larger. Same treatment as TopBar.tsx's
             * working logo: a light chip with the logo inverted to black via
             * brightness-0 (the asset itself is white-only, no dark variant). */}
            <div className="flex items-center gap-3 bg-white rounded-2xl pl-3 pr-4 py-2">
              <img
                src={`${process.env.PUBLIC_URL || ''}/panther-logo.svg`}
                alt="Panther"
                className="h-10 w-auto brightness-0"
              />
              <span className="text-sm font-bold tracking-[0.25em] text-gray-900">ACCESS</span>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-white mt-4 tracking-tight">Manuais</h2>
          <p className="text-sm text-gray-400 mt-1.5 max-w-md">
            Ficou com alguma dúvida? Consulte os manuais abaixo para aprender a usar o sistema.
          </p>
        </div>

        <div className="h-[3px] bg-gradient-to-r from-blue-500 via-blue-400/60 to-transparent" />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 sm:p-6">
        {!loading && !error && manuals.length > 0 && (
          <div className="relative max-w-6xl mx-auto mb-5">
            <Search className={cn(
              'absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none',
              lightTheme ? 'text-gray-400' : 'text-gray-500'
            )} />
            <input
              type="text"
              placeholder="Buscar manuais..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={cn(
                'w-full pl-9 pr-3 py-2.5 text-sm rounded-xl border focus:outline-none focus:ring-1 transition-colors',
                lightTheme
                  ? 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-blue-400 focus:ring-blue-200'
                  : 'bg-gray-900 border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500/20'
              )}
            />
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
          </div>
        ) : error ? (
          <div className={cn('text-sm text-center py-16', lightTheme ? 'text-gray-400' : 'text-gray-500')}>
            {error}
          </div>
        ) : manuals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <FileText className={cn('h-10 w-10', lightTheme ? 'text-gray-300' : 'text-gray-700')} />
            <p className={cn('text-sm', lightTheme ? 'text-gray-400' : 'text-gray-500')}>
              Nenhum manual disponível
            </p>
          </div>
        ) : filteredManuals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Search className={cn('h-10 w-10', lightTheme ? 'text-gray-300' : 'text-gray-700')} />
            <p className={cn('text-sm', lightTheme ? 'text-gray-400' : 'text-gray-500')}>
              Nenhum manual encontrado para "{searchTerm}"
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 max-w-6xl mx-auto">
            {filteredManuals.map(manual => (
              <div
                key={manual.id}
                role="button"
                tabIndex={0}
                onClick={() => setViewing(manual)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setViewing(manual);
                  }
                }}
                className={cn(
                  'group flex flex-col rounded-2xl border p-5 cursor-pointer transition-all duration-200',
                  lightTheme
                    ? 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-900/5'
                    : 'bg-gray-900 border-gray-800 hover:border-blue-700 hover:shadow-lg hover:shadow-blue-900/20'
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className={cn(
                    'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors',
                    lightTheme
                      ? 'bg-blue-50 text-blue-600 group-hover:bg-blue-100'
                      : 'bg-blue-500/10 text-blue-400 group-hover:bg-blue-500/15'
                  )}>
                    <FileText className="h-5 w-5" />
                  </div>
                  <a
                    href={manual.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    title="Baixar"
                    className={cn(
                      '-mr-1.5 -mt-1.5 p-2 rounded-lg transition-colors',
                      lightTheme
                        ? 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                        : 'text-gray-500 hover:text-gray-200 hover:bg-gray-800'
                    )}
                  >
                    <Download className="h-4 w-4" />
                  </a>
                </div>

                <p className={cn('text-sm font-semibold leading-snug mt-3', lightTheme ? 'text-gray-900' : 'text-gray-100')}>
                  {manual.title}
                </p>
                {manual.description && (
                  <p className={cn('text-xs mt-1.5 leading-relaxed line-clamp-2', lightTheme ? 'text-gray-500' : 'text-gray-400')}>
                    {manual.description}
                  </p>
                )}

                <span className={cn(
                  'inline-flex items-center gap-1 text-xs font-medium mt-3',
                  lightTheme ? 'text-blue-600' : 'text-blue-400'
                )}>
                  Ver manual
                  <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManuaisPanel;
